import { useCallback, useRef } from 'react';
import { TimelineItem, TimelineTrack } from '../types';
import { TIMELINE_CONSTANTS } from '../constants';
import useTimelineStore from '../stores/use-timeline-store';
import { snapToGrid } from './use-timeline-drag-and-drop/snapping-utils';
import { validateDropPosition } from './use-timeline-drag-and-drop/validation-utils';
import { calculateGhostPosition } from './use-timeline-drag-and-drop/ghost-utils';
import {
  handleDragStart as handleDragStartUtil,
  calculateResizePosition,
  calculateMovePosition,
  calculateMultiDragGhosts,
  calculateFloatingGhost
} from './use-timeline-drag-and-drop/drag-handlers';

interface UseTimelineDragAndDropProps {
  totalDuration: number;
  tracks: TimelineTrack[];
  onItemMove?: (itemId: string, newStart: number, newEnd: number, newTrackId: string) => void;
  onItemResize?: (itemId: string, newStart: number, newEnd: number) => void;
  timelineRef: React.RefObject<HTMLDivElement>;
  onInsertTrackAt?: (index: number) => string;
  onInsertMultipleTracksAt?: (index: number, count: number) => string[];
  onCreateTracksWithItems?: (
    index: number,
    trackItems: Array<{ trackId: string; items: Array<{ itemId: string; start: number; end: number }> }>
  ) => void;
  selectedItemIds?: string[];
}

export const useTimelineDragAndDrop = ({
  totalDuration,
  tracks,
  onItemMove,
  onItemResize,
  timelineRef,
  onInsertTrackAt,
  onCreateTracksWithItems,
  selectedItemIds = [],
}: UseTimelineDragAndDropProps) => {
  const {
    setDraggedItem,
    setGhostElement,
    setFloatingGhost,
    setIsValidDrop,
    setDragInfo,
    getDragInfo,
    resetDragState,
    setIsDragging,
    setInsertionIndex,
    setMagneticPreview,
    setCurrentDragPosition,
  } = useTimelineStore();

  const lastUpdateTime = useRef<number>(0);

  const handleDragStart = useCallback(
    (
      item: TimelineItem,
      clientX: number,
      clientY: number,
      action: "move" | "resize-start" | "resize-end"
    ) => {
      if (!timelineRef.current) return;

      handleDragStartUtil({
        item,
        clientX,
        clientY,
        action,
        selectedItemIds,
        tracks,
        setDragInfo,
        setDraggedItem,
        setIsDragging,
        setGhostElement,
        setIsValidDrop,
        setInsertionIndex,
        totalDuration
      });
    },
    [
      timelineRef,
      selectedItemIds,
      tracks,
      setDragInfo,
      setDraggedItem,
      setIsDragging,
      setGhostElement,
      setIsValidDrop,
      setInsertionIndex,
      totalDuration
    ]
  );

  const handleDrag = useCallback(
    (clientX: number, clientY: number) => {
      const dragInfo = getDragInfo();
      if (!dragInfo || !timelineRef.current) return;

      // Throttle updates for performance
      const now = performance.now();
      if (now - lastUpdateTime.current < 16) return; // ~60fps
      lastUpdateTime.current = now;

      const timelineRect = timelineRef.current.getBoundingClientRect();
      const deltaX = clientX - dragInfo.startX;
      const deltaY = clientY - dragInfo.startY;

      // Calculate time and track changes
      const deltaTime = (deltaX / timelineRect.width) * totalDuration;
      const trackHeight = TIMELINE_CONSTANTS.TRACK_HEIGHT;
      const rawDeltaTrack = deltaY / trackHeight;
      const deltaTrack = Math.round(rawDeltaTrack);

      let newStart: number;
      let newDuration: number;
      let newTrackIndex: number;
      let constrainedDeltaTrack = deltaTrack;

      // For multi-drag, exclude all selected items from collision detection
      const isMultiDrag = dragInfo.selectedItemsSnapshot.length > 1;
      const excludeIds = isMultiDrag
        ? dragInfo.selectedItemsSnapshot.map(s => s.id)
        : [dragInfo.id];

      if (isMultiDrag && dragInfo.action !== "move") {
        // Multi-selection only supports move operations, not resize
        return;
      }

      // Calculate new position based on action type
      if (dragInfo.action === "move") {
        const moveResult = calculateMovePosition({
          dragInfo,
          deltaTime,
          deltaTrack,
          tracks,
          excludeIds,
          totalDuration
        });
        newStart = moveResult.start;
        newDuration = moveResult.duration;
        newTrackIndex = moveResult.trackIndex;
        constrainedDeltaTrack = moveResult.constrainedDeltaTrack;
      } else {
        // Resize operations
        const resizeResult = calculateResizePosition({
          action: dragInfo.action as "resize-start" | "resize-end",
          dragInfo,
          deltaTime,
          tracks,
          excludeIds,
          totalDuration
        });
        newStart = resizeResult.start;
        newDuration = resizeResult.duration;
        newTrackIndex = dragInfo.startRow; // Don't change track during resize
      }

      // Validate drop position
      const validation = validateDropPosition(
        newStart,
        newDuration,
        newTrackIndex,
        tracks,
        excludeIds,
        getDragInfo,
        dragInfo.action
      );

      // Handle preview and adjust position
      let finalStart = newStart;
      let finalDuration = newDuration;
      let magneticPreviewData = null;

      if (validation.magneticPreview) {
        const targetTrack = tracks[newTrackIndex];

        if (validation.magneticStart !== undefined) {
          if (targetTrack.magnetic) {
            // Magnetic track with insertion position
            finalStart = validation.magneticStart;
          } else if (dragInfo.action === "resize-start") {
            // Non-magnetic track with constrained resize-start position
            finalStart = validation.magneticStart;
            // Keep the same end position, adjust duration
            const originalEnd = dragInfo.startPosition + dragInfo.startDuration;
            finalDuration = originalEnd - finalStart;
          }
        }

        magneticPreviewData = {
          trackId: targetTrack.id,
          previewItems: validation.magneticPreview
        };
      }

      // Calculate ghost elements
      let allGhostElements = [];
      let allItemsValid = validation.isValid;

      if (isMultiDrag) {
        const multiDragResult = calculateMultiDragGhosts({
          dragInfo,
          finalStart,
          finalDuration,
          constrainedDeltaTrack,
          tracks,
          totalDuration,
          excludeIds,
          getDragInfo
        });
        allGhostElements = multiDragResult.ghosts;
        allItemsValid = multiDragResult.allValid;
      } else {
        // Single item drag
        const ghostElement = calculateGhostPosition(finalStart, finalDuration, newTrackIndex, totalDuration, tracks.length);
        allGhostElements = [ghostElement];
      }

      // Batch all state updates
      setMagneticPreview(magneticPreviewData);
      setGhostElement(allGhostElements);
      setIsValidDrop(allItemsValid);
      setDragInfo({
        ...dragInfo,
        currentStart: finalStart,
        currentDuration: newDuration,
      });

      // Update current drag position for guidelines
      setCurrentDragPosition({
        start: finalStart,
        end: finalStart + finalDuration,
        trackIndex: newTrackIndex,
      });

      // Calculate floating ghost and insertion indicator
      const contentScrollTop = (timelineRef.current as HTMLDivElement).scrollTop || 0;
      const floatingGhostResult = calculateFloatingGhost({
        clientY,
        timelineRect,
        contentScrollTop,
        newDuration,
        totalDuration,
        clientX,
        allItemsValid,
        dragInfo,
        isMultiDrag,
        tracks
      });

      if (floatingGhostResult.shouldShow) {
        setFloatingGhost(floatingGhostResult.floatingGhost);
        setGhostElement(null); // Hide track-aligned ghost
        setInsertionIndex(floatingGhostResult.insertionIndex);
      } else {
        setFloatingGhost(null);
        setInsertionIndex(null);
      }
    },
    [
      getDragInfo,
      timelineRef,
      totalDuration,
      tracks,
      setGhostElement,
      setIsValidDrop,
      setFloatingGhost,
      setInsertionIndex,
      setDragInfo,
      setMagneticPreview,
      setCurrentDragPosition,
    ]
  );

  const handleDragEnd = useCallback(() => {
    const dragInfo = getDragInfo();
    if (!dragInfo) {
      return;
    }

    const state = useTimelineStore.getState();
    const ghostElements = state.ghostElement;
    const insertionIdx = state.insertionIndex;
    const isMultiDrag = dragInfo.selectedItemsSnapshot.length > 1;

    // Handle insertion of new track for multi-drag
    if ((!ghostElements || ghostElements.length === 0) && insertionIdx !== null) {
      if (isMultiDrag && onCreateTracksWithItems) {
        // For multi-drag, create tracks with items atomically
        const primarySnapshot = dragInfo.selectedItemsSnapshot.find(s => s.id === dragInfo.id);
        if (primarySnapshot) {
          const primaryFinalStart = snapToGrid(dragInfo.currentStart ?? dragInfo.startPosition);
          const deltaTime = primaryFinalStart - primarySnapshot.originalStart;

          // Group items by their original track
          const itemsByOriginalTrack = new Map<number, typeof dragInfo.selectedItemsSnapshot>();
          for (const snapshot of dragInfo.selectedItemsSnapshot) {
            const trackItems = itemsByOriginalTrack.get(snapshot.originalRow) || [];
            trackItems.push(snapshot);
            itemsByOriginalTrack.set(snapshot.originalRow, trackItems);
          }

          // Prepare track items data for atomic creation
          const sortedTrackGroups = Array.from(itemsByOriginalTrack.entries()).sort(([a], [b]) => a - b);
          const trackItemsData = sortedTrackGroups.map(([originalTrackIndex, trackItems]) => {
            const trackId = `track-${Date.now()}-${Math.random().toString(36).slice(2, 7)}-${originalTrackIndex}`;
            const items = trackItems.map(snapshot => ({
              itemId: snapshot.id,
              start: Math.max(0, snapshot.originalStart + deltaTime),
              end: Math.max(0, snapshot.originalStart + deltaTime) + snapshot.originalDuration
            }));
            return { trackId, items };
          });

          // Create tracks with items atomically
          onCreateTracksWithItems(insertionIdx, trackItemsData);
        }
      } else if (onInsertTrackAt && onItemMove) {
        // Single item insertion
        const finalStart = snapToGrid(dragInfo.currentStart ?? dragInfo.startPosition);
        const finalDuration = snapToGrid(dragInfo.currentDuration ?? dragInfo.startDuration);
        const finalEnd = finalStart + finalDuration;
        const newTrackId = onInsertTrackAt(insertionIdx);
        onItemMove(dragInfo.id, finalStart, finalEnd, newTrackId);
      }

      resetDragState();
      return;
    }

    if (!ghostElements || ghostElements.length === 0 || !state.isValidDrop) {
      resetDragState();
      return;
    }

    if (isMultiDrag && dragInfo.action === "move" && onItemMove) {
      // Multi-drag move operation
      for (let i = 0; i < dragInfo.selectedItemsSnapshot.length; i++) {
        const snapshot = dragInfo.selectedItemsSnapshot[i];
        const ghostElement = ghostElements[i];

        if (ghostElement) {
          const finalStart = snapToGrid((ghostElement.left / 100) * totalDuration);
          const finalDuration = snapToGrid((ghostElement.width / 100) * totalDuration);
          const finalTrackIndex = Math.round(ghostElement.top * tracks.length / 100);
          const finalEnd = finalStart + finalDuration;
          const targetTrack = tracks[finalTrackIndex];

          if (targetTrack) {
            onItemMove(snapshot.id, finalStart, finalEnd, targetTrack.id);
          }
        }
      }
    } else if (!isMultiDrag) {
      // Single item operation
      const ghostElement = ghostElements[0];
      const finalStart = snapToGrid((ghostElement.left / 100) * totalDuration);
      const finalDuration = snapToGrid((ghostElement.width / 100) * totalDuration);
      const finalTrackIndex = Math.round(ghostElement.top * tracks.length / 100);
      const finalEnd = finalStart + finalDuration;

      // Apply the changes
      if (dragInfo.action === "move" && onItemMove) {
        const targetTrack = tracks[finalTrackIndex];
        if (targetTrack) {
          onItemMove(dragInfo.id, finalStart, finalEnd, targetTrack.id);
        }
      } else if (
        (dragInfo.action === "resize-start" || dragInfo.action === "resize-end") &&
        onItemResize
      ) {
        onItemResize(dragInfo.id, finalStart, finalEnd);
      }
    }

    resetDragState();
  }, [
    getDragInfo,
    resetDragState,
    totalDuration,
    tracks,
    onItemMove,
    onItemResize,
    onInsertTrackAt,
    onCreateTracksWithItems,
  ]);

  return {
    handleDragStart,
    handleDrag,
    handleDragEnd,
  };
};
